import type { AgentConnectionResponse, AgentConnectionStatus } from '@contract-analyzer/api-client';
import {
  useCallback,
  useContext,
  useEffect,
  useEffectEvent,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useLocation } from 'react-router';

import { AgentConnectionCtx, type AgentConnectionController } from './agent-connection-context';
import { AgentConnectionDialog } from './agent-connection-dialog';
import { shouldCelebrateConnection } from './agent-connection-model';
import { isAgentConnectionAutoOpenPage, shouldAutoOpenAgentConnection } from './auto-open';
import { TenantCtx } from '@/hooks/tenant-context';
import { useAuth } from '@/hooks/use-auth';
import { apiClient } from '@/lib/api';

/**
 * The shell remounts when navigation crosses layout groups, so the
 * once-per-page-load guard lives at module scope rather than in a ref.
 */
let autoOpenedThisPageLoad = false;

/** While the dialog waits for a sign-in approved in another tab or window. */
const WAITING_POLL_MS = 4000;
/** Long enough for the entry's arrival and check to finish before a remount could replay them. */
const ENTRY_CELEBRATION_MS = 1200;

type PromptOrigin = 'auto' | 'manual';

interface AgentConnectionProviderProps {
  /** Release news is still loading or has claimed this page load. */
  readonly releaseNewsPending: boolean;
  readonly children: ReactNode;
}

export function AgentConnectionProvider({ releaseNewsPending, children }: AgentConnectionProviderProps) {
  const { user } = useAuth();
  const tenant = useContext(TenantCtx);
  const { pathname } = useLocation();
  const [response, setResponse] = useState<AgentConnectionResponse | null>(null);
  const [promptOrigin, setPromptOrigin] = useState<PromptOrigin | null>(null);
  const [dialogCelebrating, setDialogCelebrating] = useState(false);
  const [entryCelebrating, setEntryCelebrating] = useState(false);
  const previousStatus = useRef<AgentConnectionStatus | null>(null);
  const acknowledging = useRef(false);
  const dialogOpenRef = useRef(false);
  const userId = user?.user_id ?? null;

  const acknowledgeConnected = useCallback(() => {
    if (acknowledging.current) {
      return;
    }
    acknowledging.current = true;
    void apiClient
      .acknowledgeMyAgentConnectionConnected()
      .then(setResponse)
      .catch(() => {
        acknowledging.current = false;
      });
  }, []);

  const applyResponse = useCallback(
    (next: AgentConnectionResponse) => {
      setResponse(next);
      const summary = next.connection;
      if (summary === null) {
        return;
      }
      const celebrate = shouldCelebrateConnection({ previousStatus: previousStatus.current, connection: summary });
      previousStatus.current = summary.status;
      if (!celebrate) {
        return;
      }
      if (dialogOpenRef.current) {
        setDialogCelebrating(true);
      } else {
        setEntryCelebrating(true);
      }
      acknowledgeConnected();
    },
    [acknowledgeConnected],
  );

  const refresh = useCallback(async () => {
    try {
      applyResponse(await apiClient.getMyAgentConnection());
    } catch {
      // Optional promotion: stay silent rather than surface an error.
    }
  }, [applyResponse]);

  useEffect(() => {
    if (userId === null) {
      return;
    }
    void refresh();
  }, [refresh, userId]);

  // Sign-ins may have changed since the page loaded, as when the CLI signed out:
  // look again whenever the reader opens the dialog.
  useEffect(() => {
    if (promptOrigin === 'manual') {
      void refresh();
    }
  }, [promptOrigin, refresh]);

  const onAutoOpenPage = isAgentConnectionAutoOpenPage(pathname, tenant?.slug ?? null);
  const autoOpenDue =
    promptOrigin === null &&
    shouldAutoOpenAgentConnection({
      connection: response,
      onAutoOpenPage,
      releaseNewsPending,
      alreadyAutoOpened: autoOpenedThisPageLoad,
    });
  const dialogOpen = promptOrigin !== null || autoOpenDue;
  const status = response?.eligible === true ? (response.connection?.status ?? null) : null;

  useEffect(() => {
    dialogOpenRef.current = dialogOpen;
  }, [dialogOpen]);

  const refreshWhenVisible = useEffectEvent(() => {
    if (document.visibilityState === 'visible') {
      void refresh();
    }
  });

  // Approving the CLI sign-in happens on the app's own approval page, usually in
  // another tab: look again whenever this window regains focus.
  useEffect(() => {
    if (status === null || (status === 'connected' && !dialogOpen)) {
      return;
    }
    function onReturn() {
      refreshWhenVisible();
    }
    window.addEventListener('focus', onReturn);
    document.addEventListener('visibilitychange', onReturn);
    return () => {
      window.removeEventListener('focus', onReturn);
      document.removeEventListener('visibilitychange', onReturn);
    };
  }, [dialogOpen, status]);

  // A window beside the approval page never loses focus, so poll while waiting.
  useEffect(() => {
    if (!dialogOpen || status !== 'not_connected') {
      return;
    }
    const intervalId = window.setInterval(() => {
      refreshWhenVisible();
    }, WAITING_POLL_MS);
    return () => {
      window.clearInterval(intervalId);
    };
  }, [dialogOpen, status]);

  useEffect(() => {
    if (!entryCelebrating) {
      return;
    }
    const timeoutId = window.setTimeout(() => {
      setEntryCelebrating(false);
    }, ENTRY_CELEBRATION_MS);
    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [entryCelebrating]);

  // The dialog opens from render when the auto-open is due; the moment it
  // actually takes focus is when this page load's one auto-open is spent.
  const handleOpenAutoFocus = useCallback(() => {
    if (promptOrigin !== null) {
      return;
    }
    autoOpenedThisPageLoad = true;
    setPromptOrigin('auto');
  }, [promptOrigin]);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (open) {
        return;
      }
      // Any close of the introduction counts as seen, on every device.
      if (promptOrigin === 'auto' || autoOpenDue) {
        autoOpenedThisPageLoad = true;
        void apiClient
          .dismissMyAgentConnection()
          .then(setResponse)
          .catch(() => undefined);
      }
      if (dialogCelebrating) {
        // Hand the moment to the sidebar: the entry now shrinks where it can be seen.
        setDialogCelebrating(false);
        setEntryCelebrating(true);
      }
      setPromptOrigin(null);
    },
    [autoOpenDue, dialogCelebrating, promptOrigin],
  );

  const instructions = response?.eligible === true ? response.instructions : null;
  const connection = response?.eligible === true ? response.connection : null;
  const controller = useMemo<AgentConnectionController | null>(
    () =>
      instructions === null || connection === null
        ? null
        : {
            instructions,
            connection,
            entryStatus: dialogCelebrating ? 'not_connected' : connection.status,
            celebrate: entryCelebrating,
            openPrompt: () => {
              // Opening on purpose spends the auto-open, so it cannot pop up again after.
              autoOpenedThisPageLoad = true;
              setPromptOrigin((current) => current ?? 'manual');
            },
          },
    [connection, dialogCelebrating, entryCelebrating, instructions],
  );

  return (
    <AgentConnectionCtx value={controller}>
      {children}
      {controller === null ? null : (
        <AgentConnectionDialog
          celebrating={dialogCelebrating}
          connection={controller.connection}
          instructions={controller.instructions}
          open={dialogOpen}
          onOpenAutoFocus={handleOpenAutoFocus}
          onOpenChange={handleOpenChange}
        />
      )}
    </AgentConnectionCtx>
  );
}
